import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const { isAuthenticated, user } = useAuth();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && isAuthenticated) {
      loadChatHistory();
    }
  }, [isOpen, isAuthenticated]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const greeting = {
        message_id: Date.now(),
        message_text: isAuthenticated 
          ? `Hello ${user?.full_name}! 👋 How can I help you today?`
          : "Hello! 👋 Welcome to Local Advisor! I'm here to help you find housing, jobs, and local recommendations. What can I help you with?",
        is_from_user: false,
        created_at: new Date().toISOString()
      };
      setMessages([greeting]);
    }
  }, [isAuthenticated, isOpen, messages.length, user?.full_name]);

  const loadChatHistory = async () => {
    try {
      const response = await api.get('/chat');
      if (response.data.length > 0) {
        setMessages(response.data);
      }
    } catch (err) {
      console.error('Failed to load chat history:', err);
    }
  };

  const handleSendMessage = async (e) => {
    if (e) e.preventDefault();
    
    if (!inputMessage.trim()) return;

    const userMessage = {
      message_id: Date.now(),
      message_text: inputMessage,
      is_from_user: true,
      created_at: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    const messageToSend = inputMessage;
    setInputMessage('');
    setIsTyping(true);

    try {
      if (isAuthenticated) {
        const response = await api.post('/chat', {
          message_text: messageToSend
        });
        
        setMessages(prev => [...prev, response.data.botMessage]);
        setIsTyping(false);
      } else {
        setTimeout(() => {
          const botResponse = generateLocalResponse(messageToSend);
          const botMessage = {
            message_id: Date.now() + 1,
            message_text: botResponse,
            is_from_user: false,
            created_at: new Date().toISOString()
          };
          setMessages(prev => [...prev, botMessage]);
          setIsTyping(false);
        }, 500);
      }
    } catch (err) {
      console.error('Chat error:', err);
      setIsTyping(false);
    }
  };

  const generateLocalResponse = (message) => {
    const lowerMessage = message.toLowerCase();

    if (lowerMessage.match(/^(hi|hello|hey)/)) {
      return "Hello! 👋 Welcome to Local Advisor! I can help you find housing and jobs. Try asking me about apartments in Bucharest or available job positions!";
    }

    if (lowerMessage.includes('house') || lowerMessage.includes('apartment') || lowerMessage.includes('room')) {
      return "Looking for housing? 🏠 We have many options! Visit our Housing page to browse apartments, rooms, and shared housing across Romania. You can filter by price, location, and more!";
    }

    if (lowerMessage.includes('job') || lowerMessage.includes('work')) {
      return "Looking for work? 💼 Check out our Jobs page! We have positions ranging from customer service to IT. You can filter by job type and location!";
    }

    if (lowerMessage.includes('login') || lowerMessage.includes('register')) {
      return "To unlock all features, create an account! Click 'Sign In' at the top. With an account, you can save favorites, apply to jobs, and contact housing owners directly!";
    }

    return "I can help you with housing, jobs, and local tips! Try asking about:\n• 'Show me apartments in Bucharest'\n• 'What jobs are available?'\n• 'Help me find a room'";
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const handleQuickMessage = (msg) => {
    setInputMessage(msg);
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 bg-emerald-600 text-white p-4 rounded-full shadow-lg hover:bg-emerald-700 transition-all hover:scale-110 z-50"
        aria-label="Open chat"
      >
        <MessageCircle size={28} />
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 w-96 h-[500px] bg-white rounded-2xl shadow-2xl flex flex-col z-50">
      <div className="bg-emerald-600 text-white p-4 rounded-t-2xl flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
            <MessageCircle className="text-emerald-600" size={24} />
          </div>
          <div>
            <h3 className="font-bold">Local Advisor Bot</h3>
            <p className="text-xs text-emerald-100">Always here to help!</p>
          </div>
        </div>
        <button
          onClick={() => setIsOpen(false)}
          className="hover:bg-emerald-700 p-2 rounded-full transition-colors"
        >
          <X size={20} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
        {messages.map((message) => (
          <div
            key={message.message_id}
            className={`flex ${message.is_from_user ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                message.is_from_user
                  ? 'bg-emerald-600 text-white rounded-br-none'
                  : 'bg-white text-gray-800 shadow-md rounded-bl-none'
              }`}
            >
              <p className="text-sm whitespace-pre-line">{message.message_text}</p>
              <p
                className={`text-xs mt-1 ${
                  message.is_from_user ? 'text-emerald-100' : 'text-gray-500'
                }`}
              >
                {formatTime(message.created_at)}
              </p>
            </div>
          </div>
        ))}

        {isTyping && (
          <div className="flex justify-start">
            <div className="bg-white text-gray-800 shadow-md rounded-2xl rounded-bl-none px-4 py-3">
              <div className="flex space-x-2">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className="p-4 border-t border-gray-200 bg-white">
        <div className="flex items-center space-x-2">
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
            placeholder="Type your message..."
            className="flex-1 px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
          <button
            onClick={handleSendMessage}
            disabled={!inputMessage.trim()}
            className="bg-emerald-600 text-white p-3 rounded-full hover:bg-emerald-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            <Send size={20} />
          </button>
        </div>
      </div>

      <div className="px-4 pb-4 flex flex-wrap gap-2 bg-white rounded-b-2xl">
        <button
          onClick={() => handleQuickMessage('Show me apartments in Bucharest')}
          className="text-xs bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded-full transition-colors"
        >
          🏠 Housing
        </button>
        <button
          onClick={() => handleQuickMessage('What jobs are available?')}
          className="text-xs bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded-full transition-colors"
        >
          💼 Jobs
        </button>
        <button
          onClick={() => handleQuickMessage('Help')}
          className="text-xs bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded-full transition-colors"
        >
          ❓ Help
        </button>
      </div>
    </div>
  );
};

export default Chatbot;